import { AssignmentDTO, AssignmentDTOClient } from 'models/assignment'
import { AssignmentStage } from 'constants/assignment'
import { Auth0Roles } from 'models/auth'

/** A set containing all assignment stages considered as finished for all roles except creative */
const finishedAssignmentStages = new Set([
  AssignmentStage.VISUALS_SENT_TO_CLIENT,
])

/** A set containing all assignment stages considered as finished for creative */
const finishedCreativeAssignmentStages = new Set([
  AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR,
  AssignmentStage.VISUALS_SENT_BY_EDITOR_TO_BKBN,
  AssignmentStage.VISUALS_SENT_TO_CLIENT,
])

/** A set containing all assignment stages considered as available for creative */
const availableCreativeAssignmentStages = new Set([
  AssignmentStage.MISSION_ORDER_PLACED,
  AssignmentStage.PRE_PRODUCTION,
  AssignmentStage.AUTOMATED,
  AssignmentStage.WAITING_FOR_CT_TO_ACCEPT,
])

/** Method which decides if assignment should be marked as finished */
export const isAssignmentFinished = (assignment: AssignmentDTO, roles: Auth0Roles) => {
  if (roles.isCreative) return finishedCreativeAssignmentStages.has(assignment.stage)
  else return finishedAssignmentStages.has(assignment.stage)
}

/** Method which decides if assignment is marked as available for creative to accept */
export const isAssignmentAvailable = (assignment: AssignmentDTO) => availableCreativeAssignmentStages.has(assignment.stage)

/** Stages in which an order can still be cancelled — no line may be past them */
export const stagesUnlockingOrderCancellation = new Set([
  AssignmentStage.MISSION_ORDER_PLACED,
  AssignmentStage.PRE_PRODUCTION,
  AssignmentStage.AUTOMATED,
  AssignmentStage.WAITING_FOR_CT_TO_ACCEPT,
  AssignmentStage.WAITING_FOR_DATE_AND_TIME,
  AssignmentStage.CREATIVE_BOOKED,
  AssignmentStage.WAITING_FOR_POWER_OF_ATTORNEY_DOCUMENTS,
  AssignmentStage.PRE_PROCESSING_ORDER
])

/** The order's lines, without cancelled ones when `ignoreCancelledLines` — a cancelled line keeps no shoot */
export const liveAssignments = <T extends Pick<AssignmentDTOClient, 'isCancelled'>>(
  assignments: ReadonlyArray<T> | null | undefined,
  ignoreCancelledLines: boolean
): T[] => {
  const all = [...(assignments ?? [])]
  return ignoreCancelledLines ? all.filter(assignment => !assignment.isCancelled) : all
}

/**
 * Whether the order's lines block cancelling the order: a line past the cancellable stages blocks it.
 * With `ignoreCancelledLines`, cancelled lines (parked at their terminal stage) are skipped, and an order
 * whose every line is cancelled stays blocked — there is nothing left to cancel.
 */
export const blocksOrderCancellation = (
  assignments: ReadonlyArray<Pick<AssignmentDTOClient, 'stage' | 'isCancelled'>>,
  ignoreCancelledLines: boolean
): boolean => {
  if (!ignoreCancelledLines) return assignments.some(assignment => !stagesUnlockingOrderCancellation.has(assignment.stage))

  const liveAssignments = assignments.filter(assignment => !assignment.isCancelled)
  if (assignments.length > 0 && liveAssignments.length === 0) return true

  return liveAssignments.some(assignment => !stagesUnlockingOrderCancellation.has(assignment.stage))
}
